import { createFileRoute, useRouter, Outlet, useRouterState } from '@tanstack/react-router';
import {
  ArrowLeft,
  CreditCard,
  Tag,
  ShieldAlert,
  ChevronRight,
  CheckCircle2,
  X,
} from 'lucide-react';
import { AuditTrail } from '@/components/audit/AuditTrail';
import { AUDIT_ENTITY_TYPES } from '@/components/audit/entityTypes';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import { DetailPageLayout, useWorkspaceHeader } from '@/components/layouts';
import { DetailCard, DetailField, DetailFieldGrid } from '@/components/molecules';
import { PricebookItemActions } from '@/components/organisms/pricebook/PricebookItemActions';
import { PricebookCatalogPlacementCard } from '@/components/organisms/pricebook/detail/PricebookCatalogPlacementCard';
import { PricebookCostMarginCard } from '@/components/organisms/pricebook/detail/PricebookCostMarginCard';
import { PricebookCustomerFacingCard } from '@/components/organisms/pricebook/detail/PricebookCustomerFacingCard';
import { PricebookDetailSummary } from '@/components/organisms/pricebook/detail/PricebookDetailSummary';
import { PricebookEquipmentSpecCard } from '@/components/organisms/pricebook/detail/PricebookEquipmentSpecCard';
import { PricebookPartsCard } from '@/components/organisms/pricebook/detail/PricebookPartsCard';
import { PricebookPriceBuildupCard } from '@/components/organisms/pricebook/detail/PricebookPriceBuildupCard';
import { PricebookPricingCard } from '@/components/organisms/pricebook/detail/PricebookPricingCard';
import { PricebookSuppliersCard } from '@/components/organisms/pricebook/detail/PricebookSuppliersCard';
import { SupplierCostHistoryCard } from '@/components/organisms/pricebook/detail/SupplierCostHistoryCard';
import { PricebookUsedInCard } from '@/components/organisms/pricebook/detail/PricebookUsedInCard';
import { usePricebookItemEconomics } from '@/components/organisms/pricebook/detail/usePricebookItemEconomics';
import { useComponentTotals } from '@/hooks/pricebook/useComponentTotals';
import { useIsFieldHidden } from '@/hooks/usePermissions';
import { usePurchasingEnabled } from '@/features/purchasing/visibility';
import {
  fmt,
  isFlatPricePricebookService,
  isTimeAndMaterialsPricebookItem,
} from '@/components/organisms/pricebook/support/pricebook-helpers';
import {
  membershipPlansFromQueryData,
  useMembershipPlans,
} from '@/hooks/pricebook/useMembershipPlans';
import { usePricebookItem } from '@/hooks/pricebook/usePricebookItem';
import { usePricebookItems } from '@/hooks/pricebook/usePricebookItems';
import { usePricebookItemUsage } from '@/hooks/pricebook/usePricebookItemUsage';
import {
  useApplyDynamicPrice,
  useDynamicPricePreview,
  useMarkupProfiles,
} from '@/hooks/pricebook/useDynamicPricing';
import { useQuickBooksStatus } from '@/hooks/settings/useQuickBooksStatus';
import { useChartOfAccounts } from '@/hooks/settings/useChartOfAccounts';
import { useChartOfClasses } from '@/hooks/settings/useChartOfClasses';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { useState, useEffect, useMemo } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { formatLabel } from '@/lib/utils';
import {
  COMPONENT_DISPLAY_MODE_LABELS,
  ITEM_TYPE_LABELS,
  type DynamicPriceBreakdown,
  type PricebookItem,
} from '@/types/pricebook';

export const Route = createFileRoute('/_dashboard/dashboard/pricebook/$id')({
  component: PricebookDetailPage,
});

function formatDate(value?: string | null, timeZone?: string | null) {
  if (!value) return '—';
  const formatted = formatDateTimeInTimeZone(value, timeZone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return formatted || '—';
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function PricebookDetailPage() {
  const { id } = Route.useParams();
  const router = useRouter();
  const { location } = useRouterState();
  const timeZone = useCompanyTimezone();
  const itemQuery = usePricebookItem(id);
  const usageQuery = usePricebookItemUsage(id);
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [appliedConfirmation, setAppliedConfirmation] = useState<DynamicPriceBreakdown | null>(
    null,
  );
  const reduceMotion = useReducedMotion();
  const item = itemQuery.data;
  const dynamicPreview = useDynamicPricePreview(
    id,
    item?.type === 'service' && item.priceStrategy === 'DYNAMIC',
  );
  const applyDynamicPrice = useApplyDynamicPrice();

  useEffect(() => {
    if (!itemQuery.isLoading) return;
    const t = setTimeout(() => setShowSkeleton(true), 150);
    return () => clearTimeout(t);
  }, [itemQuery.isLoading]);

  const isExact = location.pathname === `/dashboard/pricebook/${id}`;
  // Stable across renders: `?? []` builds a fresh array every time, which
  // would re-run every memo keyed on it.
  const components = useMemo(() => item?.components ?? [], [item?.components]);
  const componentItemIds = components.map((child) => child.itemId);
  const componentItemsQuery = usePricebookItems(
    { ids: componentItemIds, limit: componentItemIds.length || 1 },
    { enabled: isExact && componentItemIds.length > 0, showErrorToast: false },
  );
  const componentItemCache = useMemo(
    () =>
      Object.fromEntries(
        (componentItemsQuery.data?.items ?? []).map((childItem) => [childItem.id, childItem]),
      ),
    [componentItemsQuery.data?.items],
  );
  // A time-and-materials service lists an hourly rate, which parts do not add
  // to, so it contributes 0 here; a flat-price service sells for its price and
  // its parts are cost, which is what `partsAreCostOnly` below states.
  //
  // A BUNDLE passes its real price and says so through `isBundle` (#2230):
  // its members are inside that price, so the totals return the price itself
  // rather than adding the members to it a second time.
  const isBundleItem = item?.type === 'bundle';
  const bundleServicePrice = isTimeAndMaterialsPricebookItem(item ?? {})
    ? 0
    : Number(item?.price ?? 0);
  // The saved service's own pricing defaults, so the detail page values its
  // parts by the same rules the editor priced them with.
  const componentTotals = useComponentTotals(
    components,
    componentItemCache,
    bundleServicePrice,
    item?.dynamicPricingConfig,
    isFlatPricePricebookService(item ?? {}),
    isBundleItem,
  );
  const membershipPlanIds = itemQuery.data?.membershipPlanIds ?? [];
  const membershipPlansQuery = useMembershipPlans(
    {},
    { enabled: isExact && itemQuery.data?.type === 'membership', showErrorToast: false },
  );

  // Resolve names referenced by structured upsell items (NOT-100).
  const upsellItems = useMemo(() => item?.upsellItems ?? [], [item?.upsellItems]);
  const upsellRefIds = useMemo(() => {
    const ids = new Set<string>();
    for (const u of upsellItems) {
      if (u.itemId) ids.add(u.itemId);
      for (const r of u.conditions?.requiresItems ?? []) ids.add(r);
    }
    return [...ids];
  }, [upsellItems]);
  const upsellRefQuery = usePricebookItems(
    { ids: upsellRefIds, limit: upsellRefIds.length || 1 },
    { enabled: isExact && upsellRefIds.length > 0, showErrorToast: false },
  );
  const upsellNameById = useMemo(
    () => new Map((upsellRefQuery.data?.items ?? []).map((i) => [i.id, i.name])),
    [upsellRefQuery.data?.items],
  );

  // GL fields store QBO account/class ids (NOT-97 pickers) — resolve them to
  // display names; legacy free-text values fall back to the raw stored value.
  const { data: qbStatus } = useQuickBooksStatus();
  const qbConnected = !!qbStatus?.connected;
  const { data: qbAccounts = [] } = useChartOfAccounts({ enabled: isExact && qbConnected });
  const { data: qbClasses = [] } = useChartOfClasses({ enabled: isExact && qbConnected });
  const accountNameById = useMemo(
    () => new Map(qbAccounts.map((a) => [a.id, a.name])),
    [qbAccounts],
  );
  const classNameById = useMemo(
    () => new Map(qbClasses.map((c) => [c.id, c.fullyQualifiedName ?? c.name])),
    [qbClasses],
  );

  // The list view hides these columns from roles that may not see them; the
  // detail page showed the same figures to everyone, which made it a way
  // around the mask rather than another view of the same catalog.
  const pricingHidden = useIsFieldHidden('PRICING');
  const profitabilityHidden = useIsFieldHidden('PROFITABILITY');
  const purchasingEnabled = usePurchasingEnabled();

  const { data: markupProfiles = [] } = useMarkupProfiles();
  const markupProfileNameById = useMemo(
    () => new Map(markupProfiles.map((profile) => [profile.id, profile.name])),
    [markupProfiles],
  );

  // One resolution of price and cost for the whole page, so the headline strip
  // and the cards below it cannot state different numbers.
  const economics = usePricebookItemEconomics(
    item,
    components.length > 0 ? componentTotals : null,
    dynamicPreview.data,
  );

  // The parts as the customer's line names them. Only resolved components can
  // contribute — an unresolved one has no name to print, and inventing one
  // would put a placeholder on a preview of a real document.
  // Parts that are never coming.
  //
  // A transport error is the obvious case; the quiet one is a SUCCESSFUL
  // response that simply omits ids the endpoint will not return — an archived,
  // deleted or otherwise inaccessible component. `allPartsResolved` stays
  // false for those forever, so keying the failure state on `isError` alone
  // left the page reporting "Loading part totals…" long after the fetch had
  // settled and nothing further was on its way.
  const componentLookupSettled =
    componentItemIds.length === 0 || componentItemsQuery.isSuccess || componentItemsQuery.isError;
  const componentLookupIncomplete =
    componentLookupSettled && componentItemIds.some((childId) => !componentItemCache[childId]);
  const partsLookupFailed = componentItemsQuery.isError || componentLookupIncomplete;

  // Only a part that would REACH the line can spoil its preview. `quantity`
  // and `showInDetail` live on the relationship, not the catalog row, so a
  // hidden consumable or a zero-quantity component is known to be irrelevant
  // without ever resolving it — and gating on the whole component list made
  // such a part claim the line could not be previewed when it renders exactly.
  const previewPartsResolved = useMemo(
    () =>
      components.every(
        (child) =>
          child.quantity <= 0 ||
          child.showInDetail === false ||
          Boolean(componentItemCache[child.itemId]),
      ),
    [components, componentItemCache],
  );

  const lineDetailParts = useMemo(
    () =>
      components
        .map((child) => {
          const childItem = componentItemCache[child.itemId];
          if (!childItem) return null;
          return {
            name: childItem.name,
            quantity: child.quantity,
            unit: childItem.unit,
            showInDetail: child.showInDetail,
          };
        })
        .filter((part): part is NonNullable<typeof part> => part != null),
    [components, componentItemCache],
  );

  if (!isExact) return <Outlet />;

  if (itemQuery.isLoading) {
    if (!showSkeleton) return null;
    return (
      <div className="px-5 py-4 space-y-4 animate-in fade-in duration-200">
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-20 w-full rounded-xl" />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2 py-16">
        <p className="text-sm">Item not found</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void router.navigate({ to: '/dashboard/pricebook', search: {} as never })}
        >
          Back
        </Button>
      </div>
    );
  }

  // ── Derived fields ─────────────────────────────────────────────────────────
  const membershipPlans = membershipPlansFromQueryData(membershipPlansQuery.data);
  const membershipPlanNameById = new Map(membershipPlans.map((plan) => [plan.id ?? '', plan.name]));
  const upsellRules = item.upsellRules ?? [];
  const aiTags = item.aiTags ?? [];
  const requiredAssets = item.requiredAssets ?? [];
  const hazardLevel = item.hazardLevel;
  const retrievalBoost = item.retrievalBoost;
  const glClass = item.glClass;
  const glCogs = item.glCogs;
  const glExpenseAccount = item.glExpenseAccount;
  const taxCode = item.taxCode;
  const membershipConfig = item.membershipConfig;
  const membershipDisplayPlanIds = Array.from(
    new Set([...(membershipConfig?.planId ? [membershipConfig.planId] : []), ...membershipPlanIds]),
  );

  const isMembership = item.type === 'membership';
  const isTimeAndMaterials = isTimeAndMaterialsPricebookItem(item);
  const isArchived = String(item.status).toUpperCase() === 'ARCHIVED';
  // A BUNDLE carries components too — they are what a COMPONENT_SUM bundle is
  // priced from — and gating on `service` meant a bundle's contents appeared
  // nowhere on its own page.
  const hasComponents =
    (item.type === 'service' || item.type === 'bundle') && components.length > 0;
  // Which card the parts table belongs to. A dynamic service's parts explain
  // its PRICE, so they sit in the buildup card; a flat service's parts are what
  // delivering it COSTS, so they sit with the cost. A bundle has neither card
  // and keeps a Parts card of its own.
  //
  // Both owners can decline to render — the cost card returns null when
  // profitability is masked or the item is a membership — and the parts must
  // not disappear with them, so those cases fall back to the standalone card.
  const buildupOwnsParts = hasComponents && isTimeAndMaterials;
  const partsCardElement = (
    <PricebookPartsCard
      item={item}
      components={components}
      itemCache={componentItemCache}
      totals={componentTotals}
      lookupFailed={partsLookupFailed}
      profitabilityHidden={profitabilityHidden}
      embedded
    />
  );
  // `service` already excludes the membership and bundle cases the cost card
  // declines to render for.
  const costCardOwnsParts =
    hasComponents && !isTimeAndMaterials && item.type === 'service' && !profitabilityHidden;
  const dynamicUpdateAvailable = dynamicPreview.data?.updateAvailable === true;
  // Which of the two derived figures actually moved. Both come off the same
  // apply, but the banner should only claim the one that changed.
  const priceMoveAvailable =
    dynamicPreview.data != null &&
    Math.abs(dynamicPreview.data.calculatedPrice - dynamicPreview.data.currentPrice) >= 0.01;
  const costMoveAvailable =
    dynamicPreview.data?.calculatedCost != null &&
    Math.abs(dynamicPreview.data.calculatedCost - (dynamicPreview.data.currentCost ?? 0)) >= 0.01;

  const hasAiSection =
    aiTags.length > 0 ||
    requiredAssets.length > 0 ||
    (hazardLevel != null && hazardLevel > 0) ||
    (retrievalBoost != null && retrievalBoost !== 1);
  const hasUpsellSection = upsellItems.length > 0 || upsellRules.length > 0;
  const hasUpsellAndAi = hasUpsellSection || hasAiSection;
  const hasTaxSection =
    item.taxable ||
    item.taxCategory ||
    taxCode ||
    item.taxLineType ||
    item.glAccount ||
    glCogs ||
    glExpenseAccount ||
    glClass;

  const usage = usageQuery.data;
  const usageLoading = usageQuery.isLoading;

  const onBack = () => void router.navigate({ to: '/dashboard/pricebook', search: {} as never });

  return (
    <DetailPageLayout key={id}>
      <PricebookDetailHeaderSlot item={item} onBack={onBack} />

      <AnimatePresence initial={false} mode="popLayout">
        {dynamicPreview.data?.updateAvailable && (
          <motion.div
            key="price-update-warning"
            layout
            initial={reduceMotion ? false : { opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -4, scale: 0.995 }}
            transition={{ duration: reduceMotion ? 0 : 0.22, ease: 'easeOut' }}
            className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">Pricing inputs changed</p>
              {/* Each figure only when it actually moves. Cost is derived from
                  the same inputs as the price and applied by the same button,
                  so a service whose parts got cheaper without shifting its
                  price is still out of date — but printing "price $399 →
                  $399" beside that reads as a broken banner. */}
              {priceMoveAvailable && (
                <p className="mt-0.5 text-sm">
                  Current price {fmt(dynamicPreview.data.currentPrice)} → suggested price{' '}
                  {fmt(dynamicPreview.data.calculatedPrice)}
                </p>
              )}
              {costMoveAvailable && (
                <p className="mt-0.5 text-sm">
                  Current cost {fmt(dynamicPreview.data.currentCost ?? 0)} → suggested cost{' '}
                  {fmt(dynamicPreview.data.calculatedCost ?? 0)}
                </p>
              )}
              {dynamicPreview.data.dependencyChanges.length > 0 && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {dynamicPreview.data.dependencyChanges.map((change, index) => (
                    <div
                      key={`${change.kind}-${change.label}-${index}`}
                      className="rounded-md border border-amber-200 bg-white/70 px-3 py-2 text-xs"
                    >
                      <p className="font-medium">{change.label}</p>
                      <p className="mt-0.5 text-amber-900">
                        <span className="line-through opacity-70">{change.before}</span>{' '}
                        <span aria-hidden>→</span>{' '}
                        <span className="font-semibold">{change.after}</span>
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button
              type="button"
              size="sm"
              disabled={applyDynamicPrice.isPending}
              onClick={() => {
                const appliedPreview = dynamicPreview.data!;
                applyDynamicPrice.mutate(item.id, {
                  onSuccess: () => setAppliedConfirmation(appliedPreview),
                });
              }}
            >
              {applyDynamicPrice.isPending ? 'Updating…' : 'Update service pricing'}
            </Button>
          </motion.div>
        )}

        {!dynamicPreview.data?.updateAvailable && appliedConfirmation && (
          <motion.div
            key="price-update-success"
            layout
            initial={reduceMotion ? false : { opacity: 0, y: 6, scale: 0.995 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
            transition={{ duration: reduceMotion ? 0 : 0.28, ease: 'easeOut' }}
            className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-emerald-950"
          >
            <div className="flex min-w-0 items-start gap-3">
              <CheckCircle2
                className="mt-0.5 size-5 shrink-0 text-emerald-700"
                aria-hidden="true"
              />
              <div>
                <p className="text-sm font-semibold">Service pricing updated</p>
                {/* Naming whichever figure moved. Applying writes the derived
                    COST as well as the price, so a cost-only update used to
                    confirm itself as "$399.00 → $399.00 is now the current
                    price" — a change the operator can see did not happen. */}
                {Math.abs(appliedConfirmation.calculatedPrice - appliedConfirmation.currentPrice) >=
                  0.01 && (
                  <p className="mt-0.5 text-sm">
                    {fmt(appliedConfirmation.currentPrice)} →{' '}
                    {fmt(appliedConfirmation.calculatedPrice)} is now the current price.
                  </p>
                )}
                {appliedConfirmation.calculatedCost != null &&
                  Math.abs(
                    appliedConfirmation.calculatedCost - (appliedConfirmation.currentCost ?? 0),
                  ) >= 0.01 && (
                    <p className="mt-0.5 text-sm">
                      {fmt(appliedConfirmation.currentCost ?? 0)} →{' '}
                      {fmt(appliedConfirmation.calculatedCost)} is now the current cost.
                    </p>
                  )}
                <p className="mt-1 text-xs text-emerald-800">
                  Existing estimates and jobs keep their original price snapshots.
                </p>
              </div>
            </div>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="size-8 text-emerald-800 hover:bg-emerald-100 hover:text-emerald-950"
              aria-label="Dismiss pricing update confirmation"
              onClick={() => setAppliedConfirmation(null)}
            >
              <X size={16} />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Summary ──────────────────────────────────────────────────────── */}
      <PricebookDetailSummary
        item={item}
        economics={economics}
        usage={usage}
        usageLoading={usageLoading}
        partsLookupFailed={partsLookupFailed}
        laborRate={isTimeAndMaterials ? item.laborRate : null}
        dynamicUpdateAvailable={dynamicUpdateAvailable}
        pricingHidden={pricingHidden}
        profitabilityHidden={profitabilityHidden}
        actions={
          <PricebookItemActions id={id} name={item.name} variant="header" isArchived={isArchived} />
        }
      />

      {/* ── Pricing ──────────────────────────────────────────────────────── */}
      {/* The item's OWN price: the stored catalog figure, or the engine's
          current one for a dynamic service. The combined figure — which
          includes sibling part lines — is a separate field on the card. */}
      <PricebookPricingCard
        item={item}
        price={isTimeAndMaterials ? (dynamicPreview.data?.currentPrice ?? item.price) : item.price}
        totalCharged={economics.revenue}
        pricingHidden={pricingHidden}
      />

      {/* ── How a dynamic price is built ─────────────────────────────────── */}
      {isTimeAndMaterials && (
        <PricebookPriceBuildupCard
          preview={dynamicPreview.data}
          isLoading={dynamicPreview.isLoading}
          isError={dynamicPreview.isError}
          config={item.dynamicPricingConfig}
          markupProfileNameById={markupProfileNameById}
          pricingHidden={pricingHidden}
          // One explanation, not two: a dynamic price is built from labour AND
          // parts, so the parts table belongs under the same heading as the
          // hours rather than in a card of its own further down the page.
          partsDetail={buildupOwnsParts ? partsCardElement : undefined}
        />
      )}

      {/* ── Cost & margin ────────────────────────────────────────────────── */}
      <PricebookCostMarginCard
        item={item}
        economics={economics}
        partsLookupFailed={partsLookupFailed}
        profitabilityHidden={profitabilityHidden}
        // A flat-price service's cost IS its labor plus its parts, so its parts
        // belong under the card that says so — the same arrangement the buildup
        // card gives a dynamic service, whose parts explain a price instead.
        partsDetail={costCardOwnsParts ? partsCardElement : undefined}
      />

      {/* ── Suppliers, and their cost history where purchasing is on (PLAT-FF-1) ── */}
      <PricebookSuppliersCard
        itemId={id}
        itemType={item.type}
        itemCost={economics.cost}
        costIsComposed={economics.costIsComposed}
        profitabilityHidden={profitabilityHidden}
      />
      {purchasingEnabled && (
        <SupplierCostHistoryCard
          itemId={id}
          itemType={item.type}
          profitabilityHidden={profitabilityHidden}
        />
      )}

      {/* ── Tax & Accounting ─────────────────────────────────────────────── */}
      {hasTaxSection && (
        <DetailCard label="Tax & Accounting">
          <DetailFieldGrid>
            <DetailField
              label="Taxable"
              value={
                item.taxable ? (
                  <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-current" /> Yes
                  </span>
                ) : (
                  <span className="text-muted-foreground">No</span>
                )
              }
            />
            {item.taxCategory && (
              <DetailField label="Tax Category" value={formatLabel(item.taxCategory)} />
            )}
            {/* An override with real tax consequences, and invisible until now:
                the engine reads it to decide whether the line is taxed as
                labor, as materials, or not at all. */}
            {item.taxLineType && (
              <DetailField label="Tax treatment" value={formatLabel(item.taxLineType)} />
            )}
            {taxCode && <DetailField label="Tax Code" value={taxCode} />}
            {/* Itemizing is not presentation only: stating each part
                separately on the document is what takes the sale out of
                bundling, so the mode decides taxability too. */}
            {item.type === 'bundle' && hasComponents && item.componentDisplayMode && (
              <DetailField
                label="Parts on documents"
                value={
                  COMPONENT_DISPLAY_MODE_LABELS[item.componentDisplayMode] ??
                  formatLabel(item.componentDisplayMode)
                }
              />
            )}
            {item.glAccount && (
              <DetailField
                label="GL Income"
                value={accountNameById.get(item.glAccount) ?? item.glAccount}
              />
            )}
            {glCogs && (
              <DetailField label="GL COGS" value={accountNameById.get(glCogs) ?? glCogs} />
            )}
            {glExpenseAccount && (
              <DetailField
                label="GL Expense"
                value={accountNameById.get(glExpenseAccount) ?? glExpenseAccount}
              />
            )}
            {glClass && (
              <DetailField label="GL Class" value={classNameById.get(glClass) ?? glClass} />
            )}
          </DetailFieldGrid>
        </DetailCard>
      )}

      {/* ── Parts / bundle contents ──────────────────────────────────────────
          A dynamically priced service states its parts inside the buildup card
          above, where the sum they feed is. Every other item — a flat service,
          a bundle — has no buildup card, so the parts stand as their own. */}
      {hasComponents && !buildupOwnsParts && !costCardOwnsParts && (
        <PricebookPartsCard
          item={item}
          components={components}
          itemCache={componentItemCache}
          totals={componentTotals}
          lookupFailed={partsLookupFailed}
          profitabilityHidden={profitabilityHidden}
        />
      )}

      {/* ── Equipment specification ──────────────────────────────────────── */}
      {/* ── Used in ──────────────────────────────────────────────────────────
          Read from the other end of the same relationship the Parts table
          states: what contains this, rather than what this contains. */}
      <PricebookUsedInCard usage={usage} isLoading={usageLoading} />

      <PricebookEquipmentSpecCard equipmentDescriptionId={item.equipmentDescriptionId} />

      {/* ── What the customer sees ───────────────────────────────────────── */}
      <PricebookCustomerFacingCard
        item={item}
        parts={lineDetailParts}
        partsResolved={previewPartsResolved}
        partsLookupFailed={partsLookupFailed}
      />

      {/* ── Catalog placement ────────────────────────────────────────────── */}
      <PricebookCatalogPlacementCard itemId={id} businessUnitIds={item.businessUnitIds} />

      {/* ── Membership ───────────────────────────────────────────────────── */}
      {isMembership && membershipDisplayPlanIds.length > 0 && (
        <DetailCard label="Membership">
          <div className="space-y-1.5">
            {membershipDisplayPlanIds.map((pid) => (
              <div key={pid} className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2">
                <CreditCard size={13} className="text-muted-foreground shrink-0" />
                <span className="text-sm">{membershipPlanNameById.get(pid) ?? pid}</span>
              </div>
            ))}
          </div>
        </DetailCard>
      )}

      {/* ── Upsell & AI ──────────────────────────────────────────────────── */}
      {hasUpsellAndAi && (
        <DetailCard label="Upsell & AI">
          <div className="space-y-3">
            {upsellItems.length > 0 && (
              <div className="space-y-1.5">
                {upsellItems.map((u, i) => {
                  const requires = u.conditions?.requiresItems ?? [];
                  return (
                    <div
                      key={`${u.itemId}-${i}`}
                      className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2"
                    >
                      <Badge
                        variant="secondary"
                        className="shrink-0 px-1.5 py-0 text-xs capitalize"
                      >
                        {u.relationshipType}
                      </Badge>
                      <span className="text-sm font-medium">
                        {upsellNameById.get(u.itemId) ?? u.itemId}
                      </span>
                      {u.reason && (
                        <span className="text-xs text-muted-foreground">· {u.reason}</span>
                      )}
                      <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                        {u.auto ? 'Auto-add' : 'Recommend'}
                        {u.relationshipType === 'upsell' && ` · weight ${u.aiWeight}`}
                      </span>
                      {requires.length > 0 && (
                        <span className="w-full text-xs text-muted-foreground">
                          Requires: {requires.map((r) => upsellNameById.get(r) ?? r).join(', ')}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {upsellRules.length > 0 && (
              <div className="space-y-1.5">
                {upsellRules.map((rule, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2">
                    <ChevronRight size={13} className="text-muted-foreground shrink-0" />
                    {/* NOT-51: `min-w-0` lets this flex child shrink below its
                     * content width so `truncate` engages; without it a long
                     * conditionLabel overflows and pushes the trailing badge /
                     * count out of the row (hidden behind the card edge). */}
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {rule.conditionLabel || `Rule ${i + 1}`}
                    </span>
                    <Badge variant="secondary" className="text-xs px-1.5 py-0 shrink-0">
                      {rule.triggerType === 'item_selected' ? 'On select' : 'Category'}
                    </Badge>
                    {rule.suggestedItemIds.length > 0 && (
                      <span className="text-xs text-muted-foreground shrink-0">
                        {rule.suggestedItemIds.length} items
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {aiTags.length > 0 && (
              <div className="space-y-1.5">
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Tag size={11} /> AI Tags
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {aiTags.map((t) => (
                    <Badge key={t} variant="secondary" className="text-xs">
                      {t}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {requiredAssets.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs text-muted-foreground">Required Assets</p>
                <div className="flex flex-wrap gap-1.5">
                  {requiredAssets.map((a) => (
                    <Badge key={a} variant="outline" className="text-xs font-mono">
                      {a}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {((hazardLevel != null && hazardLevel > 0) ||
              (retrievalBoost != null && retrievalBoost !== 1)) && (
              <DetailFieldGrid>
                {hazardLevel != null && hazardLevel > 0 && (
                  <DetailField
                    label="Hazard Level"
                    value={
                      <span className="inline-flex items-center gap-1">
                        <ShieldAlert size={13} className="text-amber-500" />
                        {hazardLevel}/5
                      </span>
                    }
                  />
                )}
                {retrievalBoost != null && retrievalBoost !== 1 && (
                  <DetailField label="Retrieval Boost" value={`×${retrievalBoost}`} />
                )}
              </DetailFieldGrid>
            )}
          </div>
        </DetailCard>
      )}

      {/* ── Activity (metadata + audit) ──────────────────────────────────── */}
      <DetailCard label="Activity">
        <DetailFieldGrid>
          <DetailField label="Created" value={formatDate(item.createdAt, timeZone)} />
          <DetailField label="Updated" value={formatDate(item.updatedAt, timeZone)} />
          {item.itemVersion != null && <DetailField label="Version" value={item.itemVersion} />}
          {/* Business units moved to Catalog placement, where they sit beside
              the browse folders — both answer who can reach this item, and an
              empty list means ALL of them rather than none. */}
        </DetailFieldGrid>
        <div className="mt-4">
          <AuditTrail entityType={AUDIT_ENTITY_TYPES.PRICEBOOK_ITEM} entityId={id} />
        </div>
      </DetailCard>
    </DetailPageLayout>
  );
}

function PricebookDetailHeaderSlot({ item, onBack }: { item: PricebookItem; onBack: () => void }) {
  const statusLabel = String(item.status).toUpperCase() === 'ARCHIVED' ? 'Archived' : 'Active';
  const contextHeader = (
    <div className="flex min-w-0 items-start gap-3">
      <Button variant="ghost" size="icon-sm" className="shrink-0 md:hidden" onClick={onBack}>
        <ArrowLeft size={16} />
      </Button>
      <div className="min-w-0">
        <h2 className="truncate text-base font-semibold">{item.sku ?? item.name}</h2>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {ITEM_TYPE_LABELS[item.type] ?? item.type} · {statusLabel}
        </p>
      </div>
    </div>
  );

  useWorkspaceHeader({ contextHeader }, [item.id, item.sku, item.name, item.type, item.status]);
  return null;
}
